<template>
  <div class="inline-editable-array-wrapper">
    <v-btn id="add-button" dark
           @click="addItem"
    >
      <v-icon small>
        mdi-plus
      </v-icon>
      <span class="ml-2 button-label">{{ $t('Add delegates') }}</span>
    </v-btn>

    <div v-on-clickaway="handleClickAway" class="inline-cards-content mt-4">
      <v-card v-for="(item, i) in items" :key="i" class="pt-3 pb-0" :ripple="false"
              :class="{'active': isItemInEditMode(item)}"
              @click="editItem($event, item)"
      >
        <div class="text-right mr-3">
          <v-btn
            icon
            class="cancel-button"
            small
            @click="deleteOrCancelEdit(item)"
          >
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </div>
        <v-card-text>
          <dropdown-select v-model="item.delegateUserId" :label="$t('user')"
                           dropdown-id="users"
                           outlined is-dense class="settings-field"
                           :disabled="!isItemInEditMode(item)"
          />
          <v-container class="px-0">
            <v-row dense>
              <v-col cols="6">
                <v-menu
                  v-model="item.delegateStartMenu"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{on, attrs}">
                    <v-text-field
                      id="delegateStart"
                      :label="$t('delegate start')"
                      class="settings-field"
                      outlined dense
                      :value="formatDate(item.startDate)"
                      v-bind="attrs"
                      :disabled="!isItemInEditMode(item)"
                      v-on="on"
                      @click:clear="item.startDate = null"
                    />
                  </template>
                  <v-date-picker
                    v-model="item.startDate"
                    :first-day-of-week="1"
                    :locale="$i18n.locale"
                    :max="item.endDate"
                    @input="item.delegateStartMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-menu
                  v-model="item.delegateEndMenu"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{on}">
                    <v-text-field
                      id="delegateEnd"
                      :label="$t('delegate end')"
                      :value="formatDate(item.endDate)"
                      class="settings-field"
                      outlined dense
                      :disabled="!isItemInEditMode(item)"
                      v-on="on"
                      @click:clear="item.endDate = null"
                    />
                  </template>
                  <v-date-picker
                    v-model="item.endDate"
                    :first-day-of-week="1"
                    :locale="$i18n.locale"
                    :min="item.startDate"
                    @input="item.delegateEndMenu = false"
                  />
                </v-menu>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col>
                <v-text-field
                  id="comment"
                  v-model="item.comment"
                  maxlength="100"
                  :label="$t('comment')"
                  class="settings-field"
                  outlined dense
                  clearable
                  :disabled="!isItemInEditMode(item)"
                  @click:clear="item.comment = null"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mixin as clickaway } from 'vue-clickaway';
import { debounce } from 'lodash';
import DropdownSelect from '@/atoms/DropdownSelect.vue';
import DelegateApi from '@/api/DelegateApi';

const delegateApi = new DelegateApi();

export default {
  components: {
    DropdownSelect,
  },
  mixins: [clickaway],
  model: {
    prop: 'items',
    event: 'items-updated',
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      items: [],
      selectItemIndex: -1,
      createMode: false,
      sortablePropItems: [],
      propItems: [],
      errorMsg: null,
    };
  },
  computed: {
  },
  watch: {
    userId() {
      this.initialize();
    },
    itemSaving() {
      this.selectItemIndex = -1;
      this.createMode = false;
    },
    errorMsg() {
      this.$emit('delegationValidationError', this.errorMsg);
    },
    items() {
      this.pushUpdate();
    },
  },
  created() {
    this.initialize();
  },

  methods: {
    handleClickAway(e) {
      const allowedClasses = ['v-list-item__title', 'v-icon notranslate mdi mdi-plus theme--dark', 'ml-2 button-label'];
      const calendarFieldItem = e.target.closest('.v-picker');
      const listItem = e.target.closest('.v-list-item');

      if (!allowedClasses.includes(e.target.classList.value) && !calendarFieldItem && !listItem) {
        this.selectItemIndex = -1;
        if (this.createMode) this.createMode = false;
        console.log('createMode', this.createMode);
      }
    },

    pushUpdate:
     debounce(function pushUpdatesDebounced() {
       const delegateItems = this.items.map((item) => ({
         id: item.id,
         userId: this.userId,
         delegateUserId: item.delegateUserId,
         startDate: item.startDate,
         endDate: item.endDate,
         comment: item.comment,
         replaceYester: item.replaceYester,
       }));
       this.$emit('delegatesUpdated', delegateItems);
     }, 500),
    initialize() {
      delegateApi.getActiveUserDelegations(this.userId).then((delegations) => {
        this.items = delegations;
      });
    },
    isItemInEditMode(item) {
      return this.items && this.items.indexOf(item) === this.selectItemIndex;
    },
    editItem(event, item) {
      const clickedItem = event.target;
      const arrayItemCardElement = clickedItem.closest('.v-card');

      // CLICK ON ARRAY COMPONENT ONLY WHEN INACTIVE
      if (arrayItemCardElement) {
        if (!arrayItemCardElement.classList.contains('active')) {
          if (this.selectItemIndex >= 0) {
            this.selectItemIndex = -1;
          } else {
            this.selectItemIndex = this.items.indexOf(item);
          }
          this.createMode = false;
        }
      }
    },
    deleteOrCancelEdit(item) {
      this.items.splice(this.selectItemIndex, 1);
      if (item.id) {
        delegateApi.deleteDelegationById(item.id);
      }
      this.selectItemIndex = -1;
      this.createMode = false;
    },
    saveItem(item) {
      if (this.validate(item)) {
        this.createMode = false;
        this.selectItemIndex = -1;
        this.pushUpdate();
        this.$emit('delegation-validation-error-cleared');
      } else {
        this.$emit('delegation-validation-error', 'overlap');
      }
    },
    addItem() {
      const newItem = {};
      if (this.items.length > 0) {
        this.items.unshift(newItem);
      } else {
        this.items = [newItem];
      }

      this.createMode = true;
      this.selectItemIndex = 0;
    },
    formatDate(dateString) {
      return dateString ? moment(dateString).format('DD.MM.YYYY') : '';
    },
    validate(item) {
      let noOverlap = true;
      for (let i = 0; i < this.items.length; i += 1) {
        if (i !== this.selectItemIndex) {
          const delegate = this.items[i];
          if (item.startDate <= delegate.endDate && item.endDate >= delegate.startDate) {
            noOverlap = false;
            break;
          }
        }
      }
      return noOverlap;
    },
  },
};
</script>
<style lang="scss" scoped>
.error-wrapper {
  position: relative !important;
  z-index: 40 !important;
}
.inline-editable-array-wrapper {
  margin: 5px 0 10px !important;
  .button-label {
    color: $yester-lila-light;
    font-size: 14px;
  }
  #add-button {
    background: $yester-button-light-background !important;
    box-shadow: none;
    margin-left: 0px !important;
    margin-right: 8px !important;
    margin-top: -5px;
    color: $yester-lila-light;
    width: auto;
    height: auto;
    border-radius: 4px;
    font-weight: 700;
    font-size: 14px;
    text-transform: none;
    padding: 5px 14px 4px 14px;
    letter-spacing: 0;
    .v-icon {
      font-size: 24px !important;
      color: $yester-lila-light !important;
    }

    &:hover, &::before {
      background: $yester-button-light-background !important;
    }
  }
  .v-card {
    background-color: transparent !important;
    border: 1px solid $yester-light-grey;
    margin-bottom: 15px !important;
    box-shadow: rgba(17, 20, 113, 0.25) 0px 2px 8px 0px;
    position: relative;

    &:last-of-type {
      margin-bottom: 0px !important;
    }
    &::before {
      background-color: transparent !important;
    }
    .cancel-button {
      background-color: white !important;
      padding: 0 !important;
      width: 22px;
      height: 22px;
      position: absolute;
      top: -13px;
      right: -8px;
      z-index: 20;

      .v-icon {
        color: $yester-readonly-color !important;
        font-size: 26px !important;
      }
    }
    &.active {
      border: 1px solid $yester-lila-light;
      box-shadow: none;

    }
  }
}
</style>
